import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { TradingSymbols } from '@monorepo/client-types';
import {
  Card,
  Flex,
  ProgressSlider,
  Statistic,
  TextComponent,
} from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import { ThemeColors } from '@monorepo/types';
import DashboardTooltip from '../../dashboard-common/dashboard-tooltip/dashboard-tooltip';

import '../progress-cards-5ers/progress-cards.scss';

interface Props {
  maxLoss: number;
  profitAndLoss: number;
  baseBalance: number;
}

const MaxLoss: FunctionComponent<Props> = ({
  maxLoss,
  profitAndLoss,
  baseBalance,
}) => {
  const { isMobile } = useResponsiveness();
  const { t } = useTranslation(['dashboardTemplates']);

  const displayProfitAndLoss = profitAndLoss >= 0 ? 0 : profitAndLoss;
  const displayMaxLoss = maxLoss * -1;

  const tooltip = t('dashboardTemplates:maximum_loss_for_account_tooltip');

  return (
    <Card
      hoverable={false}
      className={classNames(
        'progress-cards',
        'progress-cards__card',
        'progress-cards__card--max-loss',
      )}
    >
      <Flex vertical>
        <Flex justify={'space-between'}>
          <TextComponent weight={400} level={isMobile ? 5 : 3}>
            {t('dashboardTemplates:max_loss')}
          </TextComponent>
          <DashboardTooltip tooltipText={tooltip} />
        </Flex>
        <Flex align="baseline" wrap={'wrap'}>
          <TextComponent level={isMobile ? 3 : 2} weight={600}>
            <Statistic
              prefix={TradingSymbols.DOLLAR}
              value={displayProfitAndLoss}
              precision={2}
            />
          </TextComponent>
          <TextComponent level={isMobile ? 7 : 3}>
            <Statistic
              prefix={`/${TradingSymbols.DOLLAR}`}
              value={displayMaxLoss}
              precision={0}
            />
          </TextComponent>
        </Flex>

        <Flex vertical>
          <ProgressSlider
            disabled
            min={displayMaxLoss}
            max={0}
            value={profitAndLoss}
            trackColor={ThemeColors.colorAttention}
            primaryHandle={false}
            tooltip={{
              formatter: () => (
                <Statistic
                  prefix={TradingSymbols.DOLLAR}
                  value={profitAndLoss}
                  precision={2}
                />
              ),
            }}
          />

          <Flex justify="space-between">
            <Flex vertical>
              <TextComponent level={7} weight={600}>
                <Statistic
                  prefix={TradingSymbols.DOLLAR}
                  value={baseBalance ? baseBalance - maxLoss : displayMaxLoss}
                  precision={2}
                />
              </TextComponent>
              <TextComponent level={7}>
                {t('dashboardTemplates:max_loss')}
              </TextComponent>
            </Flex>
            <Flex vertical align="end">
              <TextComponent level={7} weight={600}>
                <Statistic
                  prefix={TradingSymbols.DOLLAR}
                  value={baseBalance || 0}
                  precision={2}
                />
              </TextComponent>
              <TextComponent level={7}>
                {t('dashboardTemplates:initial_balance')}
              </TextComponent>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
export default MaxLoss;
